import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  ChevronDown,
  ChevronRight,
  DollarSign,
  Layers,
  Sigma,
  Workflow,
  Clock,
  Zap,
} from 'lucide-react';
import type { LineageNode, ResearchChain } from '@/services/api';

const STATUS_COLORS: Record<string, string> = {
  completed: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  executing: 'text-brand-teal-dark bg-brand-teal/10 border-brand-teal/20',
  running: 'text-brand-teal-dark bg-brand-teal/10 border-brand-teal/20',
  ready: 'text-amber-700 bg-amber-50 border-amber-200',
  deferred: 'text-slate-600 bg-slate-100 border-slate-200',
  saturated: 'text-rose-700 bg-rose-50 border-rose-200',
  failed: 'text-rose-700 bg-rose-50 border-rose-200',
  dispatched: 'text-blue-700 bg-blue-50 border-blue-200',
};

const DOT_COLORS: Record<string, string> = {
  completed: 'bg-emerald-500',
  executing: 'bg-brand-teal',
  running: 'bg-brand-teal',
  ready: 'bg-amber-400',
  deferred: 'bg-slate-400',
  saturated: 'bg-rose-400',
  failed: 'bg-rose-500',
  dispatched: 'bg-blue-400',
};

function formatDuration(seconds?: number | null): string {
  if (!seconds) return '—';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

function AggregateCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
}) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={12} className="text-slate-400" />
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className="text-lg font-semibold text-navy-900">{value}</div>
    </div>
  );
}

function YieldByDepthSparkline({
  data,
}: {
  data: Array<{ depth: number; avg_yield: number; count: number }>;
}) {
  if (data.length === 0) return null;
  const max = Math.max(...data.map((d) => d.avg_yield), 0.0001);
  const width = 180;
  const height = 32;
  const barWidth = Math.max(8, Math.floor((width - 4) / Math.max(data.length, 1)) - 4);

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
          Yield by Depth
        </span>
        <span className="text-[10px] text-slate-400">
          {data.length} levels · max {(max * 100).toFixed(0)}%
        </span>
      </div>
      <svg width={width} height={height} className="block">
        {data.map((d, i) => {
          const h = Math.max(2, (d.avg_yield / max) * (height - 2));
          return (
            <rect
              key={d.depth}
              x={i * (barWidth + 4) + 2}
              y={height - h}
              width={barWidth}
              height={h}
              rx={1}
              className="fill-brand-teal"
            >
              <title>{`depth ${d.depth}: ${(d.avg_yield * 100).toFixed(1)}% (n=${d.count})`}</title>
            </rect>
          );
        })}
      </svg>
    </div>
  );
}

interface LineageChainVizProps {
  chain: ResearchChain;
  /** When true, render the aggregate metrics header. Default true. */
  showAggregates?: boolean;
  /** Optional callback when a node is clicked — falls back to Runs-page navigation. */
  onNodeClick?: (node: LineageNode) => void;
}

export function LineageChainViz({
  chain,
  showAggregates = true,
  onNodeClick,
}: LineageChainVizProps) {
  // Build a parent → children adjacency list for expand/collapse.
  const { childrenByParent, rootNodes } = useMemo(() => {
    const byParent = new Map<string | null, LineageNode[]>();
    const ids = new Set(chain.nodes.map((n) => n.id));
    for (const n of chain.nodes) {
      const parent = n.parent_id && ids.has(n.parent_id) ? n.parent_id : null;
      if (!byParent.has(parent)) byParent.set(parent, []);
      byParent.get(parent)!.push(n);
    }
    // Sort children by created_at ascending to render in temporal order.
    byParent.forEach((arr) =>
      arr.sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? '')),
    );
    return {
      childrenByParent: byParent,
      rootNodes: byParent.get(null) ?? [chain.root],
    };
  }, [chain]);

  return (
    <div className="space-y-4">
      {showAggregates && (
        <>
          <div className="grid grid-cols-5 gap-3">
            <AggregateCard icon={Layers} label="Depth" value={chain.total_depth} />
            <AggregateCard
              icon={Workflow}
              label="Tasks"
              value={`${chain.completed_tasks}/${chain.total_tasks}`}
            />
            <AggregateCard
              icon={Sigma}
              label="Entities"
              value={chain.total_entities_created}
            />
            <AggregateCard
              icon={Zap}
              label="Tokens"
              value={chain.total_tokens_used.toLocaleString()}
            />
            <AggregateCard
              icon={DollarSign}
              label="Cost"
              value={`$${chain.total_cost_usd.toFixed(2)}`}
            />
          </div>
          {chain.yield_by_depth.length > 0 && (
            <YieldByDepthSparkline data={chain.yield_by_depth} />
          )}
        </>
      )}

      <div className="space-y-1">
        {rootNodes.map((n) => (
          <ChainNodeRow
            key={n.id}
            node={n}
            childrenByParent={childrenByParent}
            onNodeClick={onNodeClick}
            level={0}
            defaultExpanded={true}
          />
        ))}
      </div>
    </div>
  );
}

function ChainNodeRow({
  node,
  childrenByParent,
  onNodeClick,
  level,
  defaultExpanded,
}: {
  node: LineageNode;
  childrenByParent: Map<string | null, LineageNode[]>;
  onNodeClick?: (node: LineageNode) => void;
  level: number;
  defaultExpanded: boolean;
}) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [detailOpen, setDetailOpen] = useState(false);

  const children = childrenByParent.get(node.id) ?? [];
  const hasChildren = children.length > 0;
  const pillClass = STATUS_COLORS[node.status] ?? 'bg-slate-50 text-slate-600 border-slate-200';
  const dotClass = DOT_COLORS[node.status] ?? 'bg-slate-300';

  const handleClick = () => {
    if (onNodeClick) {
      onNodeClick(node);
      return;
    }
    // Default: jump to Research Runs filtered to this node's client.
    if (node.node_type === 'research_run') {
      navigate(`/knowledge/estate/runs/${node.id}`);
    } else if (node.client_id) {
      navigate(
        `/knowledge/estate/runs?client_uid=${encodeURIComponent(node.client_id)}`,
      );
    }
  };

  return (
    <div>
      <div
        className={clsx(
          'flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2 hover:border-brand-teal/50 hover:bg-brand-teal/5 transition-colors',
        )}
        style={{ marginLeft: `${level * 20}px` }}
      >
        {hasChildren ? (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="flex-shrink-0 text-slate-400 hover:text-slate-700"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        ) : (
          <span className="w-3.5 flex-shrink-0" />
        )}
        <span className={clsx('w-1.5 h-1.5 rounded-full flex-shrink-0', dotClass)} />
        {node.research_type && (
          <span
            className={clsx('text-[10px] font-medium px-1.5 py-0.5 rounded-full border', pillClass)}
          >
            {node.research_type}
          </span>
        )}
        <button
          onClick={handleClick}
          className="text-xs text-slate-700 truncate text-left hover:text-brand-teal-dark flex-1 min-w-0"
        >
          {node.label ||
            node.target_entity_name ||
            node.target_entity_type ||
            node.id.slice(0, 8)}
        </button>
        <div className="flex items-center gap-3 text-[10px] text-slate-500 flex-shrink-0">
          <span>{node.entities_created} ent</span>
          <span>${node.cost_usd.toFixed(3)}</span>
          <span className="flex items-center gap-0.5">
            <Clock size={10} /> {formatDuration(node.duration_seconds)}
          </span>
          {node.actual_yield != null && (
            <span className="text-emerald-600 font-medium">
              {(node.actual_yield * 100).toFixed(0)}%
            </span>
          )}
          <button
            onClick={() => setDetailOpen((v) => !v)}
            className="text-slate-400 hover:text-slate-700"
            aria-label={detailOpen ? 'Hide detail' : 'Show detail'}
          >
            {detailOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        </div>
      </div>

      {detailOpen && (
        <div
          className="mt-1 mb-1.5 border-l-2 border-slate-200 pl-3 py-1.5 text-[10px] text-slate-600 space-y-0.5"
          style={{ marginLeft: `${level * 20 + 10}px` }}
        >
          {node.predicted_yield != null && (
            <div>
              predicted_yield:{' '}
              <span className="text-slate-900 font-mono">
                {(node.predicted_yield * 100).toFixed(1)}%
              </span>
            </div>
          )}
          {node.saturation_score != null && (
            <div>
              saturation:{' '}
              <span className="text-slate-900 font-mono">
                {(node.saturation_score * 100).toFixed(0)}%
              </span>
            </div>
          )}
          {node.priority_score != null && (
            <div>
              priority:{' '}
              <span className="text-slate-900 font-mono">
                {node.priority_score.toFixed(3)}
              </span>
            </div>
          )}
          {node.decision && (
            <div>
              decision: <span className="text-slate-900">{node.decision}</span>
            </div>
          )}
        </div>
      )}

      {expanded && hasChildren && (
        <div className="mt-1 space-y-1">
          {children.map((c) => (
            <ChainNodeRow
              key={c.id}
              node={c}
              childrenByParent={childrenByParent}
              onNodeClick={onNodeClick}
              level={level + 1}
              defaultExpanded={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}
